import React, { useState } from 'react';
import {
  Box,
  TextField,
  Typography,
  MenuItem,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Autocomplete,
  Divider,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { CalendarToday, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import useStyles from '../styles';
import { ContentBoxProps, ContentBoxWithAttributesProps } from './types';
import { getLeadSourceLabel } from '../../../utils/leadSource';

export const editableInput = (
  type: 'text' | 'textarea' | 'number' | 'rut' | 'email' | 'date' | 'select' | 'checkbox' | 'autocomplete' | 'year' | 'multiselect',
  value: string | number | Date | boolean | (string | number)[],
  // eslint-disable-next-line
  onChange: (value: any) => void,
  options?: { label: string; value: string | number }[],
  placeholder?: string,
  error?: string,
  disabled?: boolean,
) => {
  const baseProps = {
    fullWidth: true,
    size: 'small' as const,
  };

  switch (type) {
    case 'text':
    case 'rut':
      return (
        <TextField
          {...baseProps}
          value={value as string | number}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          error={!!error}
          helperText={error}
          disabled={disabled}
        />
      );
    case 'textarea':
      return (
        <TextField
          {...baseProps}
          multiline
          minRows={3}
          value={value as string}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          error={!!error}
          helperText={error}
          disabled={disabled}
        />
      );
    case 'number':
      return (
        <TextField
          {...baseProps}
          type="number"
          value={value as number | string}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
          inputProps={{ inputMode: 'numeric' }}
          disabled={disabled}
        />
      );
    case 'year':
      return (
        <TextField
          {...baseProps}
          type="number"
          value={value as number | string}
          placeholder={placeholder}
          onChange={(e) => {
            const inputValue = e.target.value;
            onChange(inputValue === '' ? '' : Number(inputValue));
          }}
          inputProps={{ inputMode: 'numeric', min: 2024, max: 2100 }}
          disabled={disabled}
        />
      );
    case 'email':
      return (
        <TextField
          {...baseProps}
          type="email"
          value={value as string}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          error={!!error}
          helperText={error}
          disabled={disabled}
        />
      );
    case 'date':
      return (
        <DatePicker
          label={placeholder}
          value={value ? new Date(value as string) : null}
          onChange={(date: Date | null) => onChange(date || new Date())}
          slots={{
            openPickerIcon: CalendarToday,
          }}
          slotProps={{
            textField: {
              fullWidth: true,
              variant: 'outlined',
              size: 'small',
              error: !!error,
              helperText: error,
              disabled,
              sx: {
                '& .MuiOutlinedInput-input': {
                  padding: '8px 10px',
                },
                '& .MuiInputAdornment-root': {
                  marginRight: '6px',
                },
              },
              InputProps: {
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarToday
                      sx={{
                        color: 'grey.500',
                        cursor: 'pointer',
                        fontSize: 18,
                      }}
                    />
                  </InputAdornment>
                ),
              },
            },
            openPickerButton: {
              sx: { display: 'none' },
            },
          }}
        />
      );
    case 'select':
      return (
        <TextField
          {...baseProps}
          select
          value={value as string | number}
          onChange={(e) => onChange(e.target.value)}
          error={!!error}
          helperText={error}
          disabled={disabled}
        >
          {(options || []).map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
      );
    case 'multiselect': {
      let arrayValue: (string | number)[] = [];
      if (Array.isArray(value)) {
        arrayValue = value;
      } else if (value !== undefined && value !== null) {
        arrayValue = [value as string | number];
      }
      return (
        <TextField
          {...baseProps}
          sx={{ maxWidth: '175px' }}
          select
          SelectProps={{
            multiple: true,
            value: arrayValue,
            onChange: (e) => {
              const newValue = e.target.value;
              const finalValue = Array.isArray(newValue) ? newValue : [newValue];
              onChange(finalValue as (string | number)[]);
            },
            renderValue: (selected) => {
              const selectedArray = Array.isArray(selected) ? selected : [selected];
              const labels = (options || [])
                .filter((opt) => selectedArray
                  .some((val) => String(val) === String(opt.value)))
                .map((opt) => opt.label);
              return labels.join(', ');
            },
          }}
          error={!!error}
          helperText={error}
          disabled={disabled}
        >
          {(options || []).map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
      );
    }
    case 'checkbox':
      return (
        <FormControlLabel
          control={(
            <Checkbox
              checked={Boolean(value)}
              onChange={(e) => onChange(e.target.checked)}
              disabled={disabled}
            />
          )}
          label={placeholder || ''}
        />
      );
    case 'autocomplete': {
      const currentOption = options?.find(
        (opt) => opt.value === value || String(opt.value) === String(value),
      ) || null;
      return (
        <Autocomplete
          options={options || []}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, val) => option.value === val.value}
          value={currentOption}
          onChange={(_e, newValue) => {
            const finalValue = newValue ? newValue.value : '';
            onChange(finalValue);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              {...baseProps}
              placeholder={placeholder}
              error={!!error}
              helperText={error}
              disabled={disabled}
            />
          )}
        />
      );
    }
    default:
      return (
        <TextField
          {...baseProps}
          value={value as string | number}
          onChange={(e) => onChange(e.target.value)}
          error={!!error}
          helperText={error}
          disabled={disabled}
        />
      );
  }
};

const ContentBox = (props: ContentBoxProps) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  const toggleSection = (title: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  const getLeadSourceLabelForAttr = (
    value: string | number | Date | boolean | (string | number)[],
  ): string => {
    const normalized = Array.isArray(value) && value.length > 0 ? value[0] : value;
    const strValue = String(normalized ?? '');
    const label = getLeadSourceLabel(strValue);
    if (label === '-' && strValue === '') return '';
    return label === '-' ? strValue : label;
  };

  const getDynamicTitle = (title: string, leadSource?: string) => {
    if (title === 'leadName' && leadSource) {
      switch (leadSource) {
        case 'hermano':
          return 'Nombre del hermano/a';
        case 'profesor':
          return 'Nombre del profesor/a';
        default:
          return 'Nombre';
      }
    }
    if (title === 'ambassadorCode' && leadSource) {
      switch (leadSource) {
        case 'embajador':
          return 'Código del embajador/a';
        case 'centro_alumnos':
          return 'Código del centro de alumnos';
        default:
          return 'Código';
      }
    }
    return title;
  };

  const getDynamicPlaceholder = (title: string, leadSource?: string) => {
    if (title === 'leadName' && leadSource) {
      switch (leadSource) {
        case 'hermano':
          return 'Ingresa el nombre de tu hermano/a';
        case 'profesor':
          return 'Ingresa el nombre del profesor/a';
        default:
          return 'Ingresa el nombre';
      }
    }
    if (title === 'ambassadorCode' && leadSource) {
      switch (leadSource) {
        case 'embajador':
          return 'Ingresa el código del embajador/a';
        case 'centro_alumnos':
          return 'Ingresa el código del centro de alumnos';
        default:
          return 'Ingresa el código';
      }
    }
    return title;
  };

  type AttributeItemType = {
    title: string;
    value: string | number | Date | boolean | (string | number)[];
    type:
    | 'text'
    | 'textarea'
    | 'number'
    | 'rut'
    | 'email'
    | 'date'
    | 'select'
    | 'checkbox'
    | 'autocomplete'
    | 'year'
    | 'multiselect';
    options?: { label: string; value: string | number }[];
    onChange?: (value: string | number | Date | boolean | (string | number)[]) => void;
    error?: string;
  };

  const renderAttributeValue = (attribute: AttributeItemType): React.ReactNode => {
    const { value, title } = attribute;

    if (Array.isArray(value)) {
      if (value.length === 0) return '-';

      return value.map((item, idx) => (
        <React.Fragment key={idx}>
          {idx > 0 && <br />}
          {String(item)}
        </React.Fragment>
      ));
    }

    if (title === '¿Cómo nos conociste?') {
      return getLeadSourceLabelForAttr(value);
    }

    return value || '-';
  };
  const groupAttributesInPairs = (attributes: AttributeItemType[]) => {
    const pairs: Array<[AttributeItemType | undefined, AttributeItemType | undefined]> = [];
    for (let i = 0; i < attributes.length; i += 2) {
      pairs.push([attributes[i], attributes[i + 1]]);
    }
    return pairs;
  };

  if (props.type === 'withAttributes') {
    const {
      leadSource,
      samePersonCheckbox,
      wrapperClassName,
      ...attributesProps
    } = props as ContentBoxWithAttributesProps & {
      leadSource?: string,
      samePersonCheckbox?: { label: string; checked: boolean; onChange: (checked: boolean) => void }
    };

    const isExpanded = expandedSections[attributesProps.title] ?? true;
    const attributePairs = groupAttributesInPairs(attributesProps.listOfAttributes);

    return (
      <Box className={`${classes.infoContainer} ${wrapperClassName || ''}`}>
        <Box
          onClick={isMobile ? () => toggleSection(attributesProps.title) : undefined}
          className={classes.contentHeaderRowMobile}
          sx={!isMobile ? { cursor: 'default' } : {}}
        >
          <Typography className={classes.textTitle}>
            {attributesProps.title}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {!isMobile && attributesProps.isEditMode && samePersonCheckbox
              && attributesProps.title === 'Datos del sostenedor' && (
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={samePersonCheckbox.checked}
                      onChange={(e) => samePersonCheckbox.onChange(e.target.checked)}
                    />
                  )}
                  label={samePersonCheckbox.label}
                />
            )}
            {isMobile && (
              <IconButton className={classes.arrowIconButton} sx={{ padding: 0 }}>
                {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
              </IconButton>
            )}
          </Box>
        </Box>
        {(!isMobile || isExpanded) && (
          <>
            <Divider />
            {attributesProps.title === '¿Cómo nos conociste?' && !attributesProps.isEditMode ? (
              <Box className={classes.attributePair}>
                {attributesProps.listOfAttributes
                  .filter((attr) => attr.value)
                  .map((attr, idx) => (
                    <Box className={classes.attributeItem} key={idx}>
                      <Typography className={classes.attributeValue}>
                        {attr.title === '¿Cómo nos conociste?'
                          ? getLeadSourceLabelForAttr(attr.value)
                          : attr.value}
                      </Typography>
                    </Box>
                  ))}
              </Box>
            ) : (
              <>
                {attributePairs.map((pair, pairIndex) => (
                  <React.Fragment key={pairIndex}>
                    <Box
                      className={classes.attributePair}
                    >
                      {pair.map((attribute, attrIndex) => {
                        if (!attribute) return null;
                        return (
                          <Box key={attrIndex} className={classes.attributeItem}>
                            <Typography
                              className={classes.attributeTitle}
                            >
                              {getDynamicTitle(attribute.title, leadSource)}:
                            </Typography>
                            {attributesProps.isEditMode && attribute.onChange ? (
                              editableInput(
                                attribute.type,
                                attribute.value,
                                attribute.onChange,
                                attribute.options,
                                getDynamicPlaceholder(attribute.title, leadSource),
                                attribute.error,
                                attributesProps.title === 'Datos del sostenedor'
                                && Boolean(samePersonCheckbox?.checked),
                              )
                            ) : (
                              <Typography
                                className={classes.attributeValue}
                              >
                                {renderAttributeValue(attribute)}
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                    {pairIndex === attributePairs.length - 2 && (
                      <Divider />
                    )}
                  </React.Fragment>
                ))}
              </>
            )}
            {attributesProps.isEditMode && samePersonCheckbox
              && attributesProps.title === 'Datos del sostenedor' && (
                <Box sx={{ marginTop: '16px' }}>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={samePersonCheckbox.checked}
                        onChange={(e) => samePersonCheckbox.onChange(e.target.checked)}
                      />
                    )}
                    label={samePersonCheckbox.label}
                  />
                </Box>
            )}
          </>
        )}
      </Box>
    );
  }

  if (props.type === 'withMultipleText') {
    return (
      <Box className={`${classes.infoContainer} ${props.wrapperClassName || ''}`}>
        {props.sections.map((section, index) => {
          const mainSectionKey = `multiple-${props.sections[0]?.title}-0`;
          const mainIsExpanded = expandedSections[mainSectionKey] ?? true;

          return (
            <React.Fragment key={index}>
              {index === 0 && (
                <Box
                  onClick={isMobile ? () => toggleSection(mainSectionKey) : undefined}
                  className={classes.contentHeaderRowMobile}
                  sx={!isMobile ? { cursor: 'default' } : {}}
                >
                  <Typography className={classes.textTitle}>
                    {section.title}
                  </Typography>
                  {isMobile && (
                    <IconButton className={classes.arrowIconButton} sx={{ padding: 0 }}>
                      {mainIsExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </IconButton>
                  )}
                </Box>
              )}
              {(!isMobile || mainIsExpanded) && (
                <>
                  {index === 0 && <Divider />}
                  {index > 0 && (
                    <Typography className={classes.textTitle}>
                      {section.title}
                    </Typography>
                  )}
                  {(() => {
                    if (props.isEditMode && section.isEditable) {
                      if (section.editType === 'checkbox'
                        && Array.isArray(section.subjects)
                        && section.subjects.length > 0) {
                        return (
                          <Box className={classes.attributePair}>
                            {section.subjects.map((subject) => (
                              <FormControlLabel
                                key={subject.id}
                                control={(
                                  <Checkbox
                                    checked={
                                      section.selectedSubjectIds?.includes(subject.name)
                                      || false
                                    }
                                    onChange={(e) => {
                                      const currentIds = section.selectedSubjectIds || [];
                                      const newIds = e.target.checked
                                        ? [...currentIds, subject.name]
                                        : currentIds.filter((name) => name !== subject.name);
                                      section.onChange?.(newIds);
                                    }}
                                  />
                                )}
                                label={subject.name}
                              />
                            ))}
                          </Box>
                        );
                      }
                      if (section.editType === 'textarea') {
                        return (
                          <TextField
                            fullWidth
                            multiline
                            minRows={4}
                            value={section.textContent}
                            onChange={(e) => section.onChange?.(e.target.value)}
                          />
                        );
                      }
                      return null;
                    }
                    if (Array.isArray(section.textContent)) {
                      return (
                        <Box className={classes.attributePair}>
                          {section.textContent.filter(Boolean).map((item, itemIndex) => (
                            <Typography key={itemIndex} className={classes.attributeValue}>
                              {item}
                            </Typography>
                          ))}
                        </Box>
                      );
                    }
                    return (
                      <Typography className={classes.attributeValue}>
                        {section.textContent}
                      </Typography>
                    );
                  })()}
                </>
              )}
            </React.Fragment>
          );
        })}
      </Box>
    );
  }

  if (props.type === 'withText') {
    const isExpanded = expandedSections[props.title] ?? true;

    return (
      <Box className={`${classes.infoContainer} ${props.wrapperClassName || ''}`}>
        <Box
          onClick={isMobile ? () => toggleSection(props.title) : undefined}
          className={classes.contentHeaderRowMobile}
          sx={!isMobile ? { cursor: 'default' } : {}}
        >
          <Typography className={classes.textTitle}>
            {props.title}
          </Typography>
          {isMobile && (
            <IconButton className={classes.arrowIconButton} sx={{ padding: 0 }}>
              {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          )}
        </Box>
        {(!isMobile || isExpanded) && (
          <>
            <Divider />
            {props.isEditMode && props.onChange ? (
              editableInput(
                'textarea',
                props.textContent,
                props.onChange,
                undefined,
                props.title,
              )
            ) : (
              <Typography className={classes.textContentDirect}>
                {props.textContent}
              </Typography>
            )}
          </>
        )}
      </Box>
    );
  }

  return null;
};

export default ContentBox;
